import { clockTime, relativeTime, shortDate, untilTime } from '../../shared/lib/format';
import type { TSource, TSourceAuthMethod, TSourceSecret } from './api';

export type TSourceActivity = TSource['activity'];
export type TSourceVerification = TSource['verification'];

const METHOD_KIND: Record<string, string> = {
  hmac: 'HMAC',
  api_key: 'API key',
  basic: 'Basic auth',
  ip_allowlist: 'IP allowlist',
};

// The server's `isCredentialedKind`: an IP allowlist has nothing a client could hold.
export const credentialedMethods = (methods: readonly TSourceAuthMethod[]): TSourceAuthMethod[] =>
  methods
    .filter((method) => method.enabled && method.method !== 'ip_allowlist')
    .sort((a, b) => a.position - b.position);

export const methodName = (method: TSourceAuthMethod): string => {
  const kind = METHOD_KIND[method.method] ?? method.method;
  if (!method.presetName) return kind;
  return method.presetVersion
    ? `${method.presetName} · v${method.presetVersion}`
    : method.presetName;
};

// `computedAt: null` means the rollup has not run — never print it as `0`.
export const counted = (value: number, computedAt: string | null): string | null =>
  computedAt === null ? null : value.toLocaleString('en-US');

const windowPhrase = (hours: number): string => {
  if (hours === 24) return 'the past day';
  if (hours === 1) return 'the past hour';
  return `the past ${hours}h`;
};

const many = (value: number): string => value.toLocaleString('en-US');

export type TActivityShape = 'header' | 'rate' | 'unchecked';

export const activityLine = (
  shape: TActivityShape,
  activity: TSourceActivity | undefined,
  options: { now?: number; timeZone?: string } = {},
): string | null => {
  const at = activity?.computedAt ?? null;
  const count = activity ? activity.requestsInWindow.toLocaleString('en-US') : null;
  const window = activity ? windowPhrase(activity.windowHours) : null;

  if (shape === 'rate') {
    if (!activity || at === null || count === null) return null;
    return activity.windowHours === 24 ? `${count} a day` : `${count} in ${window}`;
  }

  if (shape === 'unchecked') {
    if (!activity || at === null) return 'Traffic has not been counted yet';
    return `${count} accepted without checking in ${window} · as of ${clockTime(at, options.timeZone)}`;
  }

  if (!activity) return 'Traffic has not been counted yet';
  const last = `Last request ${relativeTime(activity.lastRequestAt, options.now ?? Date.now())}`;
  if (at === null) return `${last} · ${window} not counted yet`;
  return `${last} · ${count} in ${window}, as of ${clockTime(at, options.timeZone)}`;
};

export const megabytes = (bytes: number): string => {
  const mb = bytes / 1_048_576;
  return Number.isInteger(mb) ? `${mb} MB` : `${mb.toFixed(1)} MB`;
};

export const toBytes = (mb: number): number => Math.round(mb * 1_048_576);

export const generalSecrets = (secrets: TSourceSecret[]): TSourceSecret[] =>
  secrets.filter((secret) => !secret.customerId);

export const METHODS = ['POST', 'PUT', 'PATCH', 'DELETE', 'GET'] as const;

export type TMethod = (typeof METHODS)[number];

export const GRACE_HOURS = [1, 6, 24, 72] as const;

// Three distinct absences: never counted, counted but never called, counted and zero.
export type TVerificationState = 'not_counted' | 'never_arrived' | 'counted';

export const verificationState = (
  verification: TSourceVerification | undefined,
): TVerificationState => {
  if (!verification) return 'not_counted';

  const { checked, passed, computedAt, lastCheckedAt } = verification;
  if (computedAt === null || checked === null || passed === null) return 'not_counted';
  return lastCheckedAt === null ? 'never_arrived' : 'counted';
};

// One reconciled sentence: every number in it adds up to `checked`, so no second count can contradict it.
export const verificationLine = (verification: TSourceVerification | undefined): string => {
  if (!verification) return 'Signature checks have not been counted yet';

  const { checked, passed, computedAt, windowHours } = verification;
  if (computedAt === null || checked === null || passed === null) {
    return 'Signature checks have not been counted yet';
  }

  if (verification.lastCheckedAt === null) return 'No request has arrived to check yet';

  const window = windowPhrase(windowHours);
  if (checked === 0) return `Nothing was checked in ${window}`;

  const failed = Math.max(0, checked - passed);
  const head = `${many(checked)} checked in ${window}: ${many(passed)} verified`;
  if (failed === 0) return head;

  const kept = Math.min(failed, verification.flagged ?? 0);
  const refused = failed - kept;
  if (kept === 0) return `${head} · ${many(failed)} failed, all refused`;
  if (refused === 0) return `${head} · ${many(failed)} failed, all kept and marked unverified`;
  return `${head} · ${many(failed)} failed (${many(refused)} refused, ${many(kept)} kept and marked)`;
};

const share = (label: string, side: TSourceVerification['general']): string => {
  const checked = side.checked ?? 0;
  const passed = side.passed ?? 0;
  if (checked === 0) return `none on ${label}`;
  const verdict =
    passed === checked
      ? 'all verified'
      : passed === 0
        ? 'none verified'
        : `${many(passed)} verified`;
  return `${many(checked)} on ${label}, ${verdict}`;
};

export const verificationWhere = (verification: TSourceVerification | undefined): string | null => {
  if (!verification || verificationState(verification) !== 'counted') return null;
  if ((verification.checked ?? 0) === 0) return null;

  const { general, clients, clientAddressCount, unattributed } = verification;
  const before = unattributed ?? 0;
  if (clientAddressCount === 0 && (clients.checked ?? 0) === 0 && before === 0) return null;

  const parts = [share('the general address', general), share('client addresses', clients)];
  if (before > 0) parts.push(`${many(before)} counted before the two were told apart`);
  return parts.join(' · ');
};

export const failureModeLine = (onAuthFailure: TSource['onAuthFailure']): string =>
  onAuthFailure === 'reject'
    ? 'A request that fails is refused with 404 and not kept.'
    : 'A request that fails is kept, delivered, and marked unverified.';

// A client's own address always wins; the mode only decides the general address. The old
// copy promised "nothing is read from the body" whatever the mode was.
export const clientIdentityLine = (source: TSource): string => {
  const own = 'On a client’s own address, the address says whose it is.';
  const where = source.customerFieldFrom ?? 'body';
  switch (source.customerResolutionMode) {
    case 'by_field':
      return `${own} On the general address it is read from the ${where} field ${source.customerFieldPath ?? '—'}: an existing client matches even when the signature fails, and a new id creates a client only when it passes.`;
    case 'by_credential':
      return `${own} On the general address, by which client’s key verified it.`;
    default:
      return `${own} On the general address nobody is attached — nothing in the request is read.`;
  }
};

export const liveKeyCount = (secrets: readonly { state: string }[]): number =>
  secrets.filter((secret) => secret.state === 'active' || secret.state === 'grace').length;

// What the list shows as "needs attention": each of these is traffic going wrong or nowhere.
// `hasOpenAuthIssue` comes from the issues entity — auth issues are composed onto
// the source by the caller, not carried on the source response itself.
export const attentionReasons = (
  source: TSource,
  hasOpenAuthIssue: boolean,
  takers?: number,
): string[] => {
  if (source.state !== 'active') return [];
  const reasons: string[] = [];
  if (source.unauthenticated) reasons.push('not checked');
  if (hasOpenAuthIssue) reasons.push('verification is failing');
  else {
    const { checked, passed } = source.verification;
    if (checked !== null && passed !== null && checked > 0 && passed < checked) {
      reasons.push('some checks fail');
    }
  }
  if (takers === 0) reasons.push('nothing takes its requests');
  return reasons;
};

export const verificationMark = (
  verification: TSourceVerification | undefined,
  timeZone?: string,
): string | null => {
  const where = verificationWhere(verification);
  if (where !== null) return where;

  const computedAt = verification?.computedAt ?? null;
  if (computedAt === null) return null;
  return `as of ${clockTime(computedAt, timeZone)}`;
};

export const generalAddressRejected = (verification: TSourceVerification | undefined): number =>
  verification?.generalAddressRejections.noSecretConfigured ?? 0;

export const generalRejectionLine = (verification: TSourceVerification): string => {
  const { noSecretConfigured, windowHours } = verification.generalAddressRejections;
  const subject =
    noSecretConfigured === 1
      ? '1 request on the general address was'
      : `${many(noSecretConfigured)} requests on the general address were`;
  const object = noSecretConfigured === 1 ? 'it' : 'them';
  return `${subject} rejected in ${windowPhrase(windowHours)} — there is no secret to check ${object} against.`;
};

export const generalNeverArrived = (verification: TSourceVerification | undefined): boolean =>
  verification !== undefined &&
  verification.computedAt !== null &&
  verification.general.lastCheckedAt === null;

export interface ISecretOverlap {
  live: number;
  expiresAt: string;
}

export const secretOverlap = (secrets: TSourceSecret[]): ISecretOverlap | null => {
  const live = secrets.filter((secret) => secret.state === 'active' || secret.state === 'grace');
  const leaving = live
    .map((secret) => secret.expiresAt)
    .filter((at): at is string => Boolean(at) && typeof at === 'string')
    .sort();

  const expiresAt = leaving[0];
  if (live.length < 2 || !expiresAt) return null;
  return { live: live.length, expiresAt };
};

const COUNT_WORDS: Record<number, string> = { 2: 'Two', 3: 'Three', 4: 'Four' };

export const overlapLine = (overlap: ISecretOverlap, now: number, timeZone?: string): string => {
  const count = COUNT_WORDS[overlap.live] ?? String(overlap.live);
  const which = overlap.live === 2 ? 'the older one' : 'the oldest one';
  const day = shortDate(overlap.expiresAt, timeZone).replace(/ \d{4}$/, '');
  const at = clockTime(overlap.expiresAt, timeZone);
  return `${count} secrets are live · ${which} stops being checked on ${day} at ${at} · ${untilTime(overlap.expiresAt, now)}`;
};
